import { useMemo, useState } from 'react';
import { Link } from 'wouter';

import { colorForId, initialsOf, pluralize } from '~/client/lib/format';
import { formatDayLabel, formatRangeLabel, monthsInRange } from '~/shared/dates';
import { buildDensity, topOverlap } from '~/shared/density';
import type { PollWithRespondents, Respondent } from '~/shared/types';
import { nowIsoDay } from '~/shared/tz';

import { Calendar } from './Calendar';
import { DayBreakdown, DayPreview } from './DayBreakdown';
import { Icon } from './Icons';
import { Avatar, Button, Chip } from './Primitives';

interface HostViewProps {
  data: PollWithRespondents;
  allRespondents: Respondent[];
  excludedIds: Set<string>;
  onToggleExcluded: (id: string) => void;
  onResetExcluded: () => void;
  onShare: () => void;
  onRespondAs: () => void;
}

export function HostView({
  data,
  allRespondents,
  excludedIds,
  onToggleExcluded,
  onResetExcluded,
  onShare,
  onRespondAs,
}: HostViewProps) {
  const { poll, respondents } = data;
  const months = monthsInRange(poll.startDate, poll.endDate);
  const firstMonth = months[0] ?? poll.startDate.slice(0, 7);
  const [year, setYear] = useState(() => Number(firstMonth.slice(0, 4)));
  const [month, setMonth] = useState(() => Number(firstMonth.slice(5, 7)) - 1);
  const [breakdownDay, setBreakdownDay] = useState<string | null>(null);

  const total = respondents.length;
  const density = useMemo(() => buildDensity(respondents), [respondents]);
  const top = useMemo(() => topOverlap(density, 6), [density]);
  const todayIso = nowIsoDay(poll.timeZone);
  const hiddenCount = excludedIds.size;

  return (
    <main className="app-main">
      <div className="container">
        <div className="row between row-wrap" style={{ gap: 12, marginBottom: 24 }}>
          <div>
            <div className="row" style={{ gap: 8 }}>
              <Chip variant="lime" dot>
                live
              </Chip>
              <span className="mono muted" style={{ fontSize: 12 }}>
                /{poll.id} · {formatRangeLabel(poll.startDate, poll.endDate)}
              </span>
            </div>
            <h1 className="page-title" style={{ marginTop: 12 }}>
              {poll.title}
            </h1>
          </div>
          <div className="row" style={{ gap: 8 }}>
            <Button variant="ghost" onClick={onRespondAs}>
              <Icon name="users" size={14} /> add my dates
            </Button>
            <Button variant="primary" onClick={onShare}>
              <Icon name="share" size={14} /> share
            </Button>
          </div>
        </div>

        {hiddenCount > 0 && (
          <div className="exclusion-banner row between row-wrap" style={{ marginBottom: 16 }}>
            <span>
              showing {total} of {allRespondents.length} · <strong>{hiddenCount} hidden</strong>
            </span>
            <button type="button" className="btn btn-text btn-sm" onClick={onResetExcluded}>
              show everyone
            </button>
          </div>
        )}

        <div className="grid-2">
          <div className="card-surface" style={{ padding: 20 }}>
            <div className="row between" style={{ marginBottom: 12 }}>
              <Chip variant="soft">
                {total} {pluralize(total, 'reply', 'replies')}
              </Chip>
              <div className="density-legend">
                <span>fewer</span>
                <span className="legend-cell" style={{ background: 'rgba(200,255,74,.18)' }} />
                <span className="legend-cell" style={{ background: 'rgba(200,255,74,.55)' }} />
                <span className="legend-cell" style={{ background: 'var(--lime)' }} />
                <span>everyone</span>
              </div>
            </div>
            <Calendar
              year={year}
              month={month}
              density={density}
              total={total || 1}
              onMonthChange={(y, m) => {
                setYear(y);
                setMonth(m);
              }}
              mode="view"
              todayIso={todayIso}
              windowStart={poll.startDate}
              windowEnd={poll.endDate}
              onCellClick={setBreakdownDay}
              renderCellHover={
                respondents.length > 0
                  ? (day) => <DayPreview day={day} respondents={respondents} />
                  : undefined
              }
            />
            <div className="muted" style={{ fontSize: 12, marginTop: 12 }}>
              hover or tap a day to see who's in.
            </div>
          </div>

          <div className="col" style={{ gap: 16 }}>
            <div className="card-surface" style={{ padding: 20 }}>
              <div className="field-label">top overlap</div>
              {top.length === 0 ? (
                <p className="muted" style={{ marginTop: 12, marginBottom: 0 }}>
                  {allRespondents.length === 0
                    ? 'no replies yet — share the link to get going.'
                    : 'no overlap with this group.'}
                </p>
              ) : (
                <div className="col" style={{ gap: 4, marginTop: 8 }}>
                  {top.map(({ day, yes, maybe }) => (
                    <button
                      key={day}
                      type="button"
                      className="overlap-row"
                      onClick={() => setBreakdownDay(day)}
                    >
                      <span style={{ fontSize: 15, fontWeight: 500 }}>{formatDayLabel(day)}</span>
                      <span
                        className="mono"
                        style={{
                          fontSize: 13,
                          color: yes === total ? 'var(--ink)' : 'var(--fg-muted)',
                        }}
                      >
                        {yes}
                        {maybe > 0 ? `+${maybe}?` : ''}/{total}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="card-surface" style={{ padding: 20 }}>
              <div className="row between" style={{ marginBottom: 12 }}>
                <div className="field-label" style={{ marginBottom: 0 }}>
                  who's in
                </div>
                <span className="mono muted" style={{ fontSize: 11 }}>
                  {total} of {allRespondents.length}
                </span>
              </div>
              {allRespondents.length === 0 ? (
                <p className="muted" style={{ margin: 0, fontSize: 14 }}>
                  no replies yet.
                </p>
              ) : (
                <div className="col" style={{ gap: 6 }}>
                  {allRespondents.map((r) => {
                    const hidden = excludedIds.has(r.id);
                    return (
                      <div
                        key={r.id}
                        className={`respondent-row${hidden ? ' respondent-row-hidden' : ''}`}
                      >
                        <Avatar initials={initialsOf(r.name)} color={colorForId(r.id)} size={28} />
                        <span className="name">{r.name}</span>
                        <span className="mono muted" style={{ fontSize: 12 }}>
                          {r.yes.length}
                          {r.maybe.length > 0 ? `+${r.maybe.length}?` : ''}{' '}
                          {pluralize(r.yes.length, 'day')}
                        </span>
                        <button
                          type="button"
                          className="btn btn-text btn-sm respondent-toggle"
                          onClick={() => onToggleExcluded(r.id)}
                          aria-label={hidden ? `include ${r.name}` : `hide ${r.name}`}
                        >
                          {hidden ? 'include' : 'hide'}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
              <div className="row" style={{ marginTop: 16 }}>
                <Link href="/new" className="btn btn-text" style={{ paddingLeft: 0 }}>
                  + start another poll
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
      {breakdownDay && (
        <DayBreakdown
          day={breakdownDay}
          respondents={respondents}
          onClose={() => setBreakdownDay(null)}
        />
      )}
    </main>
  );
}
